import { subject } from '@casl/ability'
import { type IncidentReportWithCase, UserRole } from '@lmc/ts-common'
import { Button, Check, EmptyState, NoDataIcon, PageHeader } from '@lmc/ui-kit'
import { Form as AntdForm } from 'antd'
import { showToast } from 'common/utils'
import { useAbilityAccess } from 'components/AccessControl'
import { Spinner } from 'components/generic'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import useIncidentReport from 'hooks/useIncidentReport/useIncidentReport'
import useIncidentReportByLog from 'hooks/useIncidentReport/useIncidentReportByLog'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  HeaderChildrenContainer,
  Wrapper
} from './components/display/commonStyledComponents'
import IncidentCaseForm, {
  type IncidentReportEditCaseForm
} from './components/forms/IncidentCaseForm'
import CloseIncidentModal from './components/modals/CloseIncidentModal'
import IncidentTypeChangeModal from './components/modals/IncidentTypeChangeModal'
import { getNewFilesFormData } from './components/UploadAttachmentsOnSaveInput'
import {
  getDeletedAttachmentsIndexes,
  prepareUpdateBody,
  prepareUpdateInitialValues
} from './utils/incidentCaseFormUtils'

type IncidentReportEditCaseScreenProps = {
  testId?: string
}

const IncidentReportEditCaseScreen = ({
  testId = 'incident-edit-case'
}: IncidentReportEditCaseScreenProps = {}) => {
  const [form] = AntdForm.useForm()
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const [isDirty, setIsDirty] = useState(false)
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const navigate = useNavigate()
  const { id: incidentLogId } = useParams()

  const [isClosingIncident, setIsClosingIncident] = useState(false)
  const [isModalVisible, setIsModalVisible] = useState(false)

  const handleOpen = () => {
    setIsModalVisible(true)
  }

  const handleCancel = () => {
    setIsModalVisible(false)
  }

  const { incidentReport, isIncidentReportLoading } =
    useIncidentReportByLog<IncidentReportWithCase>({
      incidentLogId
    })

  const {
    updateIncidentReportCase,
    uploadIncidentReportFiles,
    deleteIncidentReportFiles
  } = useIncidentReport({
    incidentReportId: incidentReport?.id,
    incidentLogId,
    onUpdateSuccess: () => {
      showToast({
        title: t(incidentReportsConstants.CASE_UPDATED)
      })
    }
  })

  const hasRolePermissionToClose = useAbilityAccess([
    {
      i: 'delete',
      this: subject('Incident', {
        log: {
          item: incidentReport?.log.item?.id
        }
      })
    }
  ])

  if (isIncidentReportLoading)
    return <Spinner data-testid={`${testId}-spinner`} />
  if (!incidentReport)
    return (
      <EmptyState
        data-testid={`${testId}-empty-state`}
        icon={<NoDataIcon />}
        title={t(incidentReportsConstants.LOG_NOT_FOUND)}
      />
    )

  const initialValues = prepareUpdateInitialValues(incidentReport, t)

  const onSave = async (values: IncidentReportEditCaseForm<string>) => {
    try {
      setIsUpdateLoading(true)
      setIsDirty(false)

      const incidentReportEditCaseBody = prepareUpdateBody(values)
      await updateIncidentReportCase(incidentReportEditCaseBody)

      const deletedIndexes = getDeletedAttachmentsIndexes(values.attachments)
      if (deletedIndexes.length > 0) {
        await deleteIncidentReportFiles({ deletedIndexes })
      }

      const formData = getNewFilesFormData(values.attachments)
      if (formData) {
        await uploadIncidentReportFiles(formData)
      }

      navigate(`../`, { relative: 'path' })
    } catch {
      showToast({
        title: t(generalConstants.ERROR, { ns: NAMESPACE.GENERAL }),
        type: 'error'
      })
    } finally {
      setIsUpdateLoading(false)
    }
  }

  const onFinish = (values: IncidentReportEditCaseForm<string>) => {
    if (values.log.item !== incidentReport.log.item?.id) {
      handleOpen()
    } else {
      onSave(values)
    }
  }

  const onValuesChange = (values: any) => {
    if (values) {
      setIsDirty(true)
    }
  }

  return (
    <>
      <PageHeader
        testId={testId}
        sticky
        title={t(incidentReportsConstants.EDITING_INCIDENT_CASE)}
        onBack={() => navigate('../..', { relative: 'path' })}
      >
        <HeaderChildrenContainer data-testid={`${testId}-header-actions`}>
          <Button
            data-testid={`${testId}-cancel-button`}
            onClick={() => navigate('../', { relative: 'path' })}
          >
            {t(incidentReportsConstants.CANCEL)}
          </Button>
          <Button
            data-testid={`${testId}-close-incident-button`}
            onClick={() => setIsClosingIncident(true)}
            disabledByPermission={!hasRolePermissionToClose}
          >
            {t(incidentReportsConstants.CLOSE_INCIDENT)}
          </Button>
          <Button
            data-testid={`${testId}-save-button`}
            variant="primary"
            type="submit"
            onClick={form.submit}
            isLoading={isUpdateLoading}
          >
            {t(incidentReportsConstants.SAVE)} <Check color="white" />
          </Button>
        </HeaderChildrenContainer>
      </PageHeader>
      <Wrapper data-testid={`${testId}-wrapper`}>
        <IncidentCaseForm
          testId={`${testId}-case-form`}
          form={form}
          onFinish={onFinish}
          onValuesChange={onValuesChange}
          initialValues={initialValues}
          incidentReport={incidentReport}
        />
        <NavigationModal canNavigate={!isDirty} />
        <CloseIncidentModal
          data-testid={`${testId}-close-modal`}
          open={isClosingIncident}
          setIsClosingIncident={setIsClosingIncident}
          onSuccess={() => navigate(`../`, { relative: 'path' })}
          incidentReport={incidentReport}
          saveChanges={isDirty ? form.submit : undefined}
        />
        <IncidentTypeChangeModal
          data-testid={`${testId}-type-change-modal`}
          open={isModalVisible}
          handleCancel={handleCancel}
          onConfirm={() => onSave(form.getFieldsValue())}
        />
      </Wrapper>
    </>
  )
}

export default IncidentReportEditCaseScreen
